import { type FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "@privacy/translations";
import { CreateUserErrorCodes } from "../api/users.types";
import { useCreateUser } from "../api/users";
import { hasErrorCode } from "../api/errors";
import * as FormDialog from "./FormDialog";
import { EmailField, NameFields, type UserFormValues } from "./UserFormFields";

type CreateUserDialogProps = {
  defaultEmail: string;
  onCancel: () => void;
  onCreated: (userId: string) => void;
};

export const CreateUserDialog: FC<CreateUserDialogProps> = ({ onCancel, onCreated, defaultEmail }) => {
  const { t } = useTranslation();
  const createUserMutation = useCreateUser();
  const {
    handleSubmit,
    control,
    setFocus,
    setError,
    formState: { errors },
  } = useForm<UserFormValues>({ defaultValues: { email: defaultEmail.trim(), firstName: "", lastName: "" } });

  FormDialog.useAutoFocusField(setFocus, "email");

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Form
        onSubmit={handleSubmit(({ email, firstName, lastName }) =>
          createUserMutation.mutate(
            { email, firstname: firstName, lastname: lastName },
            {
              onSuccess: ({ id }) => onCreated(id),
              onError: error =>
                hasErrorCode(error, CreateUserErrorCodes.alreadyExists)
                  ? setError("email", { message: t("FormDialog.UserError") })
                  : setError("root.serverError", { type: "fallback" }),
            }
          )
        )}
      >
        <FormDialog.Title>{t("CreateUser.Dialog.Header")}</FormDialog.Title>
        <FormDialog.Content>
          <FormDialog.FormError isError={Boolean(errors.root?.serverError)} />
          <FormDialog.Fields>
            <EmailField control={control} />
            <NameFields control={control} />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onCancel} />
          <FormDialog.SubmitButton isSubmitting={createUserMutation.isPending}>
            {t("CreateUser.Dialog.Form.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
